import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Target, Puzzle, Wrench, TrendingUp, Building2 } from "lucide-react";
import { Seo, breadcrumbLd } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { CaseCard, KpiTile, MiniChart } from "@/components/site/CaseCard";
import { CtaBand } from "@/components/site/CtaBand";
import { useContent, useItem } from "@/lib/api";
import NotFound from "./NotFound";

export function Results() {
  const { items, loading } = useContent("case_studies");
  const crumbs = [["Home", "/"], ["Results & Case Studies", "/results"]];
  return (
    <>
      <Seo title="Results & Case Studies | Digital Marketing Tunbridge Wells" path="/results"
        description="Digital AdDoctor case studies: Meta Ads, Google Ads, SEO, GEO and website design projects for fitness, events, e-commerce and local businesses in Tunbridge Wells, Kent and beyond."
        jsonLd={[breadcrumbLd(crumbs)]} />
      <PageHero crumbs={crumbs} eyebrow="Results & Case Studies" lines={["Real campaigns.", "Real results."]}
        intro="Every case study covers the objective, the challenge, the strategy and what changed, with results shared with each client's approval." />
      <section className="bg-[#F5F0FF] py-20 sm:py-28" data-testid="case-library">
        <div className="mx-auto grid max-w-7xl gap-5 px-5 sm:px-8 md:grid-cols-2 lg:grid-cols-3">
          {loading && <p className="text-slate-600">Loading case studies…</p>}
          {items.map((c, i) => <Reveal key={c.slug} delay={(i % 3) * 0.06} className="h-full"><CaseCard c={c} i={i} /></Reveal>)}
        </div>
      </section>
      <CtaBand title="Could your business be next?" text="Request a free audit and find out where your marketing could perform better." />
    </>
  );
}

const Block = ({ icon: I, title, text }) => (
  <Reveal className="rounded-[24px] border border-violet-200 bg-white p-7">
    <I className="h-5 w-5 text-[#7C3AED]" />
    <h2 className="mt-4 text-lg font-extrabold text-[#13052A]">{title}</h2>
    <p className="mt-2 whitespace-pre-line text-base leading-relaxed text-slate-600">{text}</p>
  </Reveal>
);

export function CaseStudy() {
  const { slug } = useParams();
  const { item: c, loading, error } = useItem("case_studies", slug);
  if (error) return <NotFound />;
  if (loading || !c) return <div className="min-h-screen bg-[#13052A]" />;
  const crumbs = [["Home", "/"], ["Results", "/results"], [c.client, `/results/${c.slug}`]];
  return (
    <>
      <Seo title={`${c.client} Case Study | ${c.category}`} path={`/results/${c.slug}`} noindex
        description={`${c.client} (${c.industry}): ${c.highlight || c.category}. A Digital AdDoctor case study covering objectives, strategy and results.`}
        jsonLd={[breadcrumbLd(crumbs)]} />
      <PageHero crumbs={crumbs} eyebrow={`${c.industry} · ${c.category}`} lines={[c.client]} intro={c.summary}
        aside={
          <div className="rounded-[28px] border border-white/10 bg-white/5 p-6 backdrop-blur-xl">
            {c.highlight && <p className="text-2xl font-extrabold leading-tight text-white" data-testid="case-detail-highlight">{c.highlight}</p>}
            {(c.channels || []).length > 0 && <p className="mt-5 text-[11px] font-bold uppercase tracking-widest text-violet-200">Channels</p>}
            <div className="mt-3 flex flex-wrap gap-2">{(c.channels || []).map((ch) => <span key={ch} className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold">{ch}</span>)}</div>
            <div className="mt-5 grid grid-cols-2 gap-2">{(c.metrics || []).map((m) => <KpiTile key={m.label} m={m} dark />)}</div>
          </div>
        } />
      <section className="bg-[#F5F0FF] py-20 sm:py-28" data-testid="case-study-detail">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          {Array.isArray(c.chart) && c.chart.length > 0 && (
          <Reveal className="mb-6 rounded-[28px] bg-[#13052A] p-6 sm:p-8">
            <p className="text-sm font-bold text-white">Performance overview</p>
            <div className="mt-5"><MiniChart data={c.chart} height={240} dark /></div>
          </Reveal>
          )}
          <div className="grid gap-5 md:grid-cols-2">
            <Block icon={Target} title="Business objectives" text={c.objective} />
            <Block icon={Puzzle} title="Marketing challenges" text={c.challenge} />
            <Block icon={Wrench} title="Strategy & implementation" text={c.strategy} />
            <Block icon={TrendingUp} title="Results achieved" text={c.results} />
          </div>
          <div className="mt-5"><Block icon={Building2} title="Business impact" text={c.impact} /></div>
          <Reveal className="mt-10 text-center">
            <Link to="/contact?type=audit" className="btn-primary" data-testid="case-study-cta">Get results like these: request a free audit <ArrowUpRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
